import { useQueries } from '@tanstack/react-query';
import { UserContext } from 'contexts/ConfigContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { formatNumWithKMBAlt, getConfig } from 'utils/helperMethods';
import { useProfitPoolOverviewContext } from '../context/ProfitPoolOverviewContext';
import { QueryObserverResult } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from '../apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { TableColumnsType } from 'antd';
import { startCase, uniqBy } from 'lodash';
import useProfitPoolFiltersLoading from './useProfitPoolFiltersLoading';

const getColumnDefinition = (kpiName: string, kpiLabelMappingForPPGBreakdown: any, tierName: string) => {
    return {
        title: kpiLabelMappingForPPGBreakdown[kpiName].replace('_tier_', `${tierName}`),
        children: [
            {
                title: 'Current Year',
                dataIndex: `${kpiName}_cy`,
                // sorter: (a: any, b: any) => {
                //     return Number(a[`${kpiName}_cy_val`]) - Number(b[`${kpiName}_cy_val`]);
                // },
                width: 130,
                align: 'center',
            },
            {
                title: 'Year Ago',
                dataIndex: `${kpiName}_ya`,
                // sorter: (a: any, b: any) => {
                //     return Number(a[`${kpiName}_ya_val`]) - Number(b[`${kpiName}_ya_val`]);
                // },
                width: 130,
                align: 'center',
            },
            {
                title: 'Difference',
                dataIndex: `${kpiName}_diff`,
                render: (value: any, record: any, index: any) => {
                    const diff = record[`${kpiName}_cy_val`] - record[`${kpiName}_ya_val`];
                    const diffPercent = ((diff / record[`${kpiName}_ya_val`]) * 100).toFixed(1);
                    return (
                        <p className={`${diff < 0 ? 'text-[#FF4D4F]' : 'text-[#4CBC38]'}`}>
                            {isNaN(Number(diffPercent)) || !isFinite(Number(diffPercent)) ? '0.0%' : `${diffPercent}%`}
                        </p>
                    );
                },
                sorter: (a: any, b: any) => {
                    const adiff = a[`${kpiName}_cy_val`] - a[`${kpiName}_ya_val`];
                    const adiffPercent = ((adiff / a[`${kpiName}_ya_val`]) * 100).toFixed(1);
                    const bdiff = b[`${kpiName}_cy_val`] - b[`${kpiName}_ya_val`];
                    const bdiffPercent = ((bdiff / b[`${kpiName}_ya_val`]) * 100).toFixed(1);
                    return Number(adiffPercent) - Number(bdiffPercent);
                },
                width: 100,
                align: 'center',
            },
        ],
    };
};

interface BrandMetrics {
    attributeDesc: string;

    volume_sales_equivalent_ya?: string;
    volume_sales_equivalent_ya_val?: number;

    profit_absolute_ya?: string;
    profit_absolute_ya_val?: number;

    volume_sales_equivalent_cy?: string;
    volume_sales_equivalent_cy_val?: number;

    profit_absolute_cy?: string;
    profit_absolute_cy_val?: number;
}

const useMultiBreakdownTable = () => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { growthKpisTierMapping, kpiLabelMappingForPPGBreakdown } = profitPoolConfig;

    const [offset, setOffset] = useState(0);
    const [breakdownTableData, setBreakdownTableData] = useState<any[]>([]);
    const [breakDownTableDataMap, setBreakDownTableDataMap] = useState<Map<string, any>>(new Map());
    const [breakDownTableDefinition, setBreakDownTableDefinition] = useState<TableColumnsType<BrandMetrics>>([
        {
            title: 'Brands',
            dataIndex: 'attributeDesc',
            key: 'attributeDesc',
            fixed: 'left',
            width: 180,
        },
    ]);

    const { selectedSubFilter } = useProfitPoolOverviewContext();
    const {
        singleSelectedTier,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
        profitPoolTierList,
    } = useContext(FilterContextData);

    // Reset data when filter changes
    useEffect(() => {
        setOffset(0);
        setBreakdownTableData([]);
        setBreakDownTableDataMap(new Map());
    }, [selectedSubFilter]);

    const isFiltersLoading = useProfitPoolFiltersLoading();

    const fetchNextPage = () => {
        setOffset((prevOffset) => prevOffset + PROFIT_POOL_API_LIMIT);
    };

    const {
        data: newData,
        isPending,
        isError,
        isSuccess,
    } = useQueries({
        queries: [true, false].flatMap((_yearAgo: boolean) => {
            return growthKpisTierMapping[singleSelectedTier[0]].map((_kpi: string) => {
                return {
                    queryKey: [
                        'ppo-multi-breakdown-table',
                        selectedSubFilter?.dataKey,
                        _kpi,
                        offset,
                        _yearAgo,
                        singleSelectedTier,
                        idForRestOfComponent,
                        timePeriodSelection,
                    ],
                    queryFn: ({ signal }: { signal?: AbortSignal }) =>
                        fetchProfitPoolData({
                            selectedSubFilter,
                            limit: PROFIT_POOL_API_LIMIT,
                            offset,
                            kpi: _kpi,
                            tierList: singleSelectedTier,
                            yearAgo: _yearAgo,
                            groupedByList: ['attribute'],
                            dimIdsFilter: idForRestOfComponent,
                            frequency: 'M',
                            timePeriod: JSON.parse(timePeriodSelection),
                            signal,
                        }),
                    enabled: Boolean(
                        allDimAndAttributeTypeList &&
                            'loading' in allDimAndAttributeTypeList &&
                            !timePeriodLoading &&
                            !idForRestOfComponentLoading &&
                            timePeriodSelection,
                    ),
                };
            });
        }),
        combine: (results: QueryObserverResult<any, any>[]) => {
            const flattenedResults = results.map((result) => result.data);
            const queryOrder = [true, false].flatMap((_yearAgo: boolean) => {
                return growthKpisTierMapping[singleSelectedTier[0]].map((_kpi: string) => {
                    return `${_kpi}${_yearAgo ? '@yearAgo' : ''}`;
                });
            });

            const finalMap = new Map();

            flattenedResults.map((_result, index) => {
                const currentKpi = queryOrder[index].split('@')[0];
                const currentYearAgo = queryOrder[index].includes('yearAgo');
                const currentKpiKey = currentYearAgo ? `${currentKpi}_ya` : `${currentKpi}_cy`;

                _result?.data?.map((_val: any, _index: number) => {
                    const createdVal = {
                        attributeDesc: _val.attribute_ex_id,
                        [currentKpiKey]: formatNumWithKMBAlt(_val.kpi_values?.[currentKpi] ?? 0),
                        [`${currentKpiKey}_val`]: _val.kpi_values?.[currentKpi] ?? 0,
                    };
                    if (!finalMap.has(_val?.attribute_ex_id)) {
                        finalMap.set(_val?.attribute_ex_id, createdVal);
                    } else {
                        const newVal = {
                            ...finalMap.get(_val?.attribute_ex_id),
                            ...createdVal,
                        };
                        finalMap.set(_val?.attribute_ex_id, newVal);
                    }
                });
            });

            return {
                data: Array.from(finalMap.values()),
                isPending: results.some((result) => result.isPending),
                isError: results.some((result) => result.isError),
                isSuccess: results.every((result) => result.isSuccess),
            };
        },
    });

    useEffect(() => {
        const columnDefinition = growthKpisTierMapping[singleSelectedTier?.[0]].map((kpi: string) => {
            return getColumnDefinition(
                kpi,
                kpiLabelMappingForPPGBreakdown,
                profitPoolTierList.find((t) => t.tierReference === singleSelectedTier?.[0])?.tierName,
            );
        });

        setBreakDownTableDefinition(
            uniqBy(
                [
                    ...[
                        {
                            title: startCase(selectedSubFilter?.dataKey),
                            dataIndex: 'attributeDesc',
                            key: 'attributeDesc',
                            fixed: 'left',
                            width: 180,
                        },
                    ],
                    ...columnDefinition,
                ],
                'title',
            ),
        );
    }, [selectedSubFilter, singleSelectedTier, growthKpisTierMapping, kpiLabelMappingForPPGBreakdown]);

    useEffect(() => {
        if (isSuccess && newData && newData.length > 0) {
            if (offset === 0) {
                // Reset data when offset is 0 (initial load or reset)
                const updatedMap = new Map();
                newData.forEach((item: any) => {
                    updatedMap.set(item.attributeDesc, item);
                });
                setBreakDownTableDataMap(updatedMap);
                setBreakdownTableData(newData);
            } else {
                // Merge new data with existing data when offset > 0
                // Use functional updates to avoid stale closures
                setBreakDownTableDataMap((prevMap) => {
                    const updatedMap = new Map(prevMap);

                    newData.forEach((item: any) => {
                        const brandId = item.attributeDesc;
                        if (updatedMap.has(brandId)) {
                            // Merge with existing data (in case same item appears with updated KPI values)
                            const existingItem = updatedMap.get(brandId);
                            updatedMap.set(brandId, {
                                ...existingItem,
                                ...item,
                            });
                        } else {
                            // Add new item (new page of data)
                            updatedMap.set(brandId, item);
                        }
                    });

                    return updatedMap;
                });

                // Update the array using the map to ensure consistency
                setBreakdownTableData((prevData) => {
                    const updatedMap = new Map();
                    // Rebuild map from previous data
                    prevData.forEach((item: any) => {
                        updatedMap.set(item.attributeDesc, item);
                    });

                    // Merge new data
                    newData.forEach((item: any) => {
                        const brandId = item.attributeDesc;
                        if (updatedMap.has(brandId)) {
                            // Merge with existing data
                            updatedMap.set(brandId, {
                                ...updatedMap.get(brandId),
                                ...item,
                            });
                        } else {
                            // Add new item
                            updatedMap.set(brandId, item);
                        }
                    });

                    return Array.from(updatedMap.values());
                });
            }
        }
    }, [newData, isSuccess, offset]);

    const isDataSyncing = isSuccess && newData.length > 0 && breakdownTableData.length === 0;
    const isLoading = isPending || isFiltersLoading || isDataSyncing;

    return {
        isError,
        data: breakdownTableData,
        tableDef: breakDownTableDefinition,
        isPending,
        isLoading,
        isSuccess,
        fetchNextPage,
    };
};

export default useMultiBreakdownTable;
